// DTO → view-model translation for the orders module. Every snake_case
// field stops here; downstream code reads only the camelCase `Order`
// view model from `types/orderTypes.ts`.

import type { PaginatedResponse } from '@shared/types/pagination'
import { pickString } from '@shared/utils'

import type {
  InvestorOfferingOrder,
  Order,
  OrderDetail,
  OrderDetailInvestorInfo,
  OrderFeeLine,
  OrderSettlement,
  OrderStatus,
  OrderTimelineEvent,
  VaspListingReadiness,
  VaspListingReadinessStatusCount,
  VaspOrder,
  VaspProcessingStatusCounts,
  CancelRejectedListingResult,
  OfferingOrders,
} from '../types/orderTypes'
import type {
  InvestorOfferingOrderItemDto,
  OrderDetailDto,
  OrderFeeLineDto,
  OrderDto,
  OrderListDto,
  OrderSettlementDto,
  OrderSummaryDto,
  OrderTimelineEventDto,
  VaspListingReadinessDto,
  VaspListingReadinessStatusCountDto,
  VaspOrderDto,
  VaspProcessingStatusCountsDto,
  CancelRejectedListingDto,
  OfferingOrdersDto,
} from './orderContracts'

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : value == null ? fallback : String(value)

const num = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

// The order's fee snapshot. Orders placed before the snapshot existed carry
// no `fees` key at all — those parse to an empty list, never a fabricated row.
const parseFeeLine = (dto: OrderFeeLineDto): OrderFeeLine => ({
  code: str(dto.code),
  label: str(dto.label),
  rateLabel: dto.rateLabel ?? null,
  netAmount: str(dto.netAmount, '0.00'),
  vatAmount: str(dto.vatAmount, '0.00'),
  grossAmount: str(dto.grossAmount, '0.00'),
})

const parseFees = (value: unknown): OrderFeeLine[] =>
  Array.isArray(value) ? value.map((line) => parseFeeLine(line as OrderFeeLineDto)) : []

function parseTimelineEvent(dto: OrderTimelineEventDto): OrderTimelineEvent {
  return {
    id: str(dto.id),
    orderStatusCategory: str(dto.order_status_category),
    statusKey: str(dto.status_key),
    fromStatusLabel: dto.from_status_label,
    statusLabel: str(dto.status_label),
    transitionedAt: str(dto.transitioned_at),
    triggeredBy: str(dto.triggered_by),
    adminName: str(dto.admin_name),
    reason: dto.reason,
  }
}

export function parseOrder(dto: OrderDto): Order {
  const summary = dto.order_summary ?? dto
  const investor = dto.investor_details ?? dto

  return {
    id: str(
      (summary as OrderSummaryDto).id ??
        (summary as OrderSummaryDto)._id ??
        (summary as OrderSummaryDto).order_id ??
        dto.id ??
        dto._id,
    ),
    orderReference: str(summary.order_reference ?? dto.order_reference),
    propertyId: str(summary.property_id ?? dto.property_id),
    propertyName: str(summary.property_name ?? dto.property_name),
    propertyImage: str(summary.property_image ?? dto.property_image),
    investorId: str(investor.investor_id ?? dto.investor_id),
    investorName: str(investor.investor_name ?? dto.investor_name),
    investorType: str(investor.investor_type ?? dto.investor_type),
    investorEmail: str(investor.email ?? dto.email),
    investorPhone: str(investor.phone ?? dto.phone),
    tokens: num(summary.tokens ?? dto.tokens),
    grossAmount: '0.00',
    fees: [],
    investmentValue: str(summary.investment_value ?? dto.investment_value, '0.00'),
    tokenPrice: str(summary.token_price ?? dto.token_price, '0.00'),
    listingValue: str(summary.listing_value ?? dto.listing_value, '0.00'),
    status: str(
      summary.order_status ?? summary.status ?? dto.status ?? dto.order_status,
      'pending',
    ),
    statusLabel: str(summary.order_status_label ?? dto.order_status_label),
    orderProcessingStatus: str(summary.order_processing_status ?? dto.order_processing_status),
    orderRefundStatus: (summary.order_refund_status ?? dto.order_refund_status ?? null) as
      | string
      | null,
    refundAt: (summary.refund_at ?? dto.refund_at ?? null) as string | null,
    orderDate: str(summary.order_date ?? dto.order_date),
    submittedAt: (summary.submitted_at ?? dto.submitted_at ?? null) as string | null,
    rejectedAt: (summary.rejected_at ?? dto.rejected_at ?? null) as string | null,
    cancelledAt: (summary.cancelled_at ?? dto.cancelled_at ?? null) as string | null,
    orderStatusReason: (summary.order_status_reason ?? dto.order_status_reason ?? null) as
      | string
      | null,
    paymentDate: summary.payment_date ?? dto.payment_date ?? null,
    paymentMethod: summary.payment_method ?? dto.payment_method ?? null,
    paymentReference:
      summary.payment_reference ??
      summary.utr_number ??
      dto.payment_reference ??
      dto.utr_number ??
      null,
    walletContribution: str(summary.wallet_contribution ?? dto.wallet_contribution, '0.00'),
    bankContribution: str(
      summary.bank_contribution ??
        dto.bank_contribution ??
        summary.investment_value ??
        dto.investment_value,
      '0.00',
    ),
    lastUpdated: str(summary.last_updated ?? dto.last_updated),
    timeline: (dto.order_timeline ?? []).map(parseTimelineEvent),
    // Legacy snake_case envelope — never carries the VASP or trustline joins.
    // Explicit nulls rather than omissions: `trustlineStatus` and
    // `trustlinePeerAuthorized` are required on `Order`, and null is the honest
    // value here — this envelope has no trustline data, so nothing is known.
    // (Both were previously omitted, which is what the two `Property
    // 'trustlineStatus' is missing` type errors on this file were reporting.)
    vaspOrderId: null,
    vaspStatus: null,
    vaspOrderType: null,
    vaspStatusUpdatedAt: null,
    vaspLastError: null,
    trustlineStatus: null,
    trustlinePeerAuthorized: null,
  }
}

// List rows arrive in either casing — collapse both with pickString.
function parseOrderListItem(raw: Record<string, unknown>): Order {
  return {
    id: pickString(raw, 'id', '_id', 'order_id'),
    orderReference: pickString(raw, 'orderReference', 'order_reference'),
    propertyId: pickString(raw, 'propertyId', 'property_id'),
    propertyName: pickString(raw, 'propertyName', 'property_name'),
    propertyImage: pickString(raw, 'propertyImage', 'property_image'),
    investorId: pickString(raw, 'investorId', 'investor_id'),
    // Null-preserving: '' and null mean the same thing to pickString, but here
    // "no trustline row" is the state the action keys off, so it is read raw.
    trustlineStatus: (raw['trustlineStatus'] as string | null | undefined) ?? null,
    // Also null-preserving, and for a stronger reason: this flag is three-state.
    // `?? null` (never `?? false`) keeps "the ledger was never read" distinct
    // from "read, and the issuer has not authorised" — see
    // @shared/constants/trustlineAuthorization.
    trustlinePeerAuthorized: (raw['trustlinePeerAuthorized'] as boolean | null | undefined) ?? null,
    investorName: pickString(raw, 'investorName', 'investor_name'),
    investorType: pickString(raw, 'investorType', 'investor_type'),
    investorEmail: pickString(raw, 'email'),
    investorPhone: pickString(raw, 'phone'),
    tokens: num(raw['tokens']),
    grossAmount: '0.00',
    fees: [],
    investmentValue: pickString(raw, 'investmentValue', 'investment_value') || '0.00',
    tokenPrice: pickString(raw, 'tokenPrice', 'token_price') || '0.00',
    listingValue: pickString(raw, 'listingValue', 'listing_value') || '0.00',
    status: (pickString(raw, 'orderStatus', 'order_status', 'status') || 'pending') as OrderStatus,
    statusLabel: pickString(raw, 'orderStatusLabel', 'order_status_label'),
    orderProcessingStatus: pickString(raw, 'orderProcessingStatus', 'order_processing_status'),
    orderRefundStatus: (raw['orderRefundStatus'] ?? raw['order_refund_status'] ?? null) as
      | string
      | null,
    refundAt: (raw['refundAt'] ?? raw['refund_at'] ?? null) as string | null,
    orderDate: pickString(raw, 'orderDate', 'order_date'),
    submittedAt: (raw['submittedAt'] ?? raw['submitted_at'] ?? null) as string | null,
    rejectedAt: (raw['rejectedAt'] ?? raw['rejected_at'] ?? null) as string | null,
    cancelledAt: (raw['cancelledAt'] ?? raw['cancelled_at'] ?? null) as string | null,
    orderStatusReason: (raw['orderStatusReason'] ?? raw['order_status_reason'] ?? null) as
      | string
      | null,
    paymentDate: (raw['paymentDate'] ?? raw['payment_date'] ?? null) as string | null,
    paymentMethod: (raw['paymentMethod'] ?? raw['payment_method'] ?? null) as string | null,
    paymentReference: (raw['paymentReference'] ??
      raw['payment_reference'] ??
      raw['utrNumber'] ??
      raw['utr_number'] ??
      null) as string | null,
    walletContribution: pickString(raw, 'walletContribution', 'wallet_contribution') || '0.00',
    bankContribution:
      pickString(
        raw,
        'bankContribution',
        'bank_contribution',
        'investmentValue',
        'investment_value',
      ) || '0.00',
    lastUpdated: pickString(raw, 'lastUpdated', 'last_updated'),
    timeline: [],
    vaspOrderId: (raw['vaspOrderId'] ?? null) as string | null,
    vaspStatus: (raw['vaspStatus'] ?? null) as string | null,
    vaspOrderType: (raw['vaspOrderType'] ?? null) as string | null,
    vaspStatusUpdatedAt: (raw['vaspStatusUpdatedAt'] ?? null) as string | null,
    vaspLastError: (raw['vaspLastError'] ?? null) as string | null,
  }
}

export function parseVaspOrder(dto: VaspOrderDto): VaspOrder {
  return {
    id: str(dto.id),
    orderId: str(dto.orderId),
    provider: str(dto.vaspProvider),
    vaspOrderId: str(dto.vaspOrderId),
    listingId: dto.vaspListingId ?? null,
    instrumentId: dto.vaspInstrumentId ?? null,
    organisationId: dto.vaspOrganisationId ?? null,
    tokenQuantity: num(dto.tokenQuantity),
    pricePerToken: str(dto.pricePerToken, '0.00'),
    value: str(dto.value, '0.00'),
    currency: str(dto.currency, 'AED'),
    orderType: dto.vaspOrderType ?? null,
    status: dto.vaspStatus ?? null,
    orderDate: dto.vaspOrderDate ?? null,
    createdAt: dto.createdAt ?? null,
    updatedAt: dto.updatedAt ?? null,
    lastError: dto.lastError ?? null,
    lastErrorSource: dto.lastErrorSource ?? null,
    lastRequestId: dto.lastRequestId ?? null,
    lastSyncedAt: dto.lastSyncedAt ?? null,
  }
}

// `all` comes from the server rather than being summed here: it is the queue's own
// row count and the per-status buckets do not add up to it, so a client-side sum
// would silently drift. Only `all` has a reader today - AppSidebar's "VAISP
// processing" badge - since the queue's KPI strip was removed.
export function parseVaspProcessingStatusCounts(
  dto: VaspProcessingStatusCountsDto,
): VaspProcessingStatusCounts {
  return {
    all: num(dto.all),
    pending: num(dto.pending),
    awaitingIssuance: num(dto.awaitingIssuance),
    distributingTokens: num(dto.distributingTokens),
    tokensDistributed: num(dto.tokensDistributed),
    awaitingComplete: num(dto.awaitingComplete),
    failed: num(dto.failed),
  }
}

function parseInvestorOfferingOrder(dto: InvestorOfferingOrderItemDto): InvestorOfferingOrder {
  return {
    id: str(dto.id),
    orderReference: str(dto.orderReference),
    tokenCount: str(dto.tokenCount, '0'),
    tokenPrice: str(dto.tokenPrice, '0.00'),
    grossAmount: str(dto.grossAmount, '0.00'),
  }
}

/**
 * Null-through rather than defaulted: an order still in flight has no holding,
 * and a zeroed placeholder would render as a settlement that happened.
 */
function parseOrderSettlement(dto: OrderSettlementDto | null | undefined): OrderSettlement | null {
  if (!dto?.holdingId) return null
  return {
    holdingId: str(dto.holdingId),
    recordedAt: str(dto.recordedAt),
    tokenQuantity: str(dto.tokenQuantity, '0'),
    titleDeedFileName: dto.titleDeedFileName ?? null,
    titleDeedIssuedAt: dto.titleDeedIssuedAt ?? null,
    // `=== true` and `?? 0`: an older API build that does not send these must
    // read as "we have no bytes, nothing tried" — the state that offers a fetch
    // — rather than as a downloadable deed the download would then 404 on.
    titleDeedIsDownloadable: dto.titleDeedIsDownloadable === true,
    titleDeedRetryCount: dto.titleDeedRetryCount ?? 0,
  }
}

export function parseOrderDetail(dto: OrderDetailDto): OrderDetail {
  const summary = dto.orderSummary ?? {}
  const investorDetails = dto.investorDetails ?? {}
  // The API may return investor fields nested under `.investor` or flat on
  // `investorDetails` directly. Merge both shapes so the parser handles either.
  const investor = investorDetails.investor ?? investorDetails
  const kyc = investorDetails.kyc ?? {}

  // Name resolution order:
  //   1. nested investor.name (single-field name)
  //   2. nested investor.firstName + investor.lastName
  //   3. flat investorDetails.name / investorDetails.investorName
  //   4. flat investorDetails.firstName + investorDetails.lastName
  const resolvedName =
    str(investor.name) ||
    [investor.firstName, investor.lastName].filter(Boolean).join(' ') ||
    str(investorDetails.name) ||
    str(investorDetails.investorName) ||
    [investorDetails.firstName, investorDetails.lastName].filter(Boolean).join(' ') ||
    '—'

  const investorInfo: OrderDetailInvestorInfo = {
    id: str(investor.id ?? investorDetails.id),
    investorType: str(investor.investorType ?? investorDetails.investorType),
    firstName: str(investor.firstName ?? investorDetails.firstName),
    lastName: str(investor.lastName ?? investorDetails.lastName),
    fullName: resolvedName,
    email: str(investor.email ?? investorDetails.email),
    phone: str(investor.phone ?? investorDetails.phone),
    marketName: str(investor.marketName ?? investorDetails.marketName),
    nationalityCountryName: str(
      investor.nationalityCountryName ?? investorDetails.nationalityCountryName,
    ),
    hasBankAccountLinked: investorDetails.hasBankAccountLinked ?? false,
    totalInvestedAmount: str(investorDetails.totalInvestedAmount, '0.00'),
    isInvestmentEligible: investorDetails.isInvestmentEligible ?? false,
    kycStatus: str(kyc.kycStatus),
    kycApprovedAt: kyc.approvedAt ?? null,
    offeringAlreadyInvested: str(investorDetails.offeringAlreadyInvested, '0.00'),
    offeringAlreadyInvestedTokens: str(investorDetails.offeringAlreadyInvestedTokens, '0'),
    offeringTotalTokens: str(investorDetails.offeringTotalTokens, '0'),
    offeringHoldingTokens: str(investorDetails.offeringHoldingTokens, '0'),
    offeringHoldingPercentage: str(investorDetails.offeringHoldingPercentage, '0'),
  }

  return {
    id: str(summary.id),
    orderReference: str(summary.orderReference),
    propertyId: str(summary.propertyId),
    propertyName: str(summary.propertyName),
    propertyImage: str(summary.propertyImage),
    investorId: str(investor.id ?? investorDetails.id),
    investorName: resolvedName,
    investorType: str(investor.investorType ?? investorDetails.investorType),
    investorEmail: str(investor.email ?? investorDetails.email),
    investorPhone: str(investor.phone ?? investorDetails.phone),
    tokens: num(summary.tokens),
    grossAmount: str(summary.grossAmount, '0.00'),
    fees: parseFees(summary.fees),
    investmentValue: str(summary.investmentValue, '0.00'),
    tokenPrice: str(summary.tokenPrice, '0.00'),
    listingValue: str(summary.listingValue, '0.00'),
    status: str(summary.orderStatus, 'pending') as OrderStatus,
    statusLabel: str(summary.orderStatus),
    orderProcessingStatus: str(summary.orderProcessingStatus),
    orderRefundStatus: (summary.orderRefundStatus ?? null) as string | null,
    refundAt: (summary.refundAt ?? null) as string | null,
    orderDate: str(summary.orderDate),
    submittedAt: summary.submittedAt ?? null,
    rejectedAt: null,
    cancelledAt: (summary.cancelledAt ?? null) as string | null,
    orderStatusReason: (summary.orderStatusReason ?? null) as string | null,
    paymentMethod: summary.paymentMethod ?? null,
    paymentReference: summary.paymentReference ?? summary.utrNumber ?? null,
    walletContribution: str(summary.walletContribution, '0.00'),
    bankContribution: str(summary.bankContribution ?? summary.investmentValue, '0.00'),
    paymentDate: summary.paymentDate ?? null,
    lastUpdated: str(summary.lastUpdated),
    timeline: (dto.orderTimeline ?? []).map((e) => ({
      id: str(e.id),
      orderStatusCategory: str(e.orderStatusCategory),
      statusKey: str(e.statusKey),
      fromStatusLabel: e.fromStatusLabel ?? null,
      statusLabel: str(e.statusLabel),
      transitionedAt: str(e.transitionedAt),
      triggeredBy: str(e.triggeredBy),
      adminName: str(e.adminName),
      reason: e.reason ?? null,
    })),
    investorInfo,
    investedOrders: (dto.investorOfferingOrders ?? []).map(parseInvestorOfferingOrder),
    vaspOrder: dto.vaspOrder ? parseVaspOrder(dto.vaspOrder) : null,
    settlement: parseOrderSettlement(dto.settlement),
    // The detail endpoint carries the full nested vaspOrder above; the row-level
    // mirror is derived from it so both surfaces agree.
    vaspOrderId: dto.vaspOrder?.vaspOrderId ?? null,
    vaspStatus: dto.vaspOrder?.vaspStatus ?? null,
    vaspOrderType: dto.vaspOrder?.vaspOrderType ?? null,
    vaspStatusUpdatedAt: dto.vaspOrder?.updatedAt ?? null,
    vaspLastError: dto.vaspOrder?.lastError ?? null,
    // Trustline axis, from the same join the list row reads. `trustlineStatus`
    // was previously omitted here — that was the second `Property
    // 'trustlineStatus' is missing` type error on this file, and it left the
    // drawer with no trustline data at all.
    trustlineStatus: summary.trustlineStatus ?? null,
    // Three-state, so `?? null` and never `?? false`: an unread ledger must not
    // read as "the issuer has not authorised". This is the flag ORD-05 surfaces.
    trustlinePeerAuthorized: summary.trustlinePeerAuthorized ?? null,
  }
}

export function parseOrderList(dto: OrderListDto): PaginatedResponse<Order> {
  // Resolve the raw rows from whichever envelope shape the backend sent.
  let rawRows: Record<string, unknown>[]
  if (dto.result) {
    rawRows = dto.result as unknown as Record<string, unknown>[]
  } else if (Array.isArray(dto.data)) {
    rawRows = dto.data as unknown as Record<string, unknown>[]
  } else if (dto.data && 'rows' in dto.data && Array.isArray(dto.data.rows)) {
    rawRows = dto.data.rows as unknown as Record<string, unknown>[]
  } else if (dto.rows) {
    rawRows = dto.rows as unknown as Record<string, unknown>[]
  } else {
    rawRows = []
  }

  const totalItems = dto.meta?.total ?? rawRows.length
  const limit = dto.meta?.limit ?? rawRows.length
  const page = dto.meta?.page ?? 1
  const totalPages =
    dto.meta?.totalPages ?? dto.meta?.total_pages ?? (limit > 0 ? Math.ceil(totalItems / limit) : 1)

  return {
    data: rawRows.map(parseOrderListItem),
    pagination: { page, limit, totalItems, totalPages },
  }
}

// ─── VAISP listing readiness (LST-04) ────────────────────────────────────────

/**
 * `vaspStatus` is carried through with `?? null` and never defaulted to a
 * string: null is the "never registered with Ctrl-Alt" bucket, and collapsing
 * it into an empty status would merge it with the orders Ctrl-Alt is still
 * working on — the two need opposite operator actions.
 */
function parseVaspListingReadinessStatusCount(
  dto: VaspListingReadinessStatusCountDto,
): VaspListingReadinessStatusCount {
  return {
    vaspStatus: dto.vaspStatus ?? null,
    isMissingVaspOrder: dto.isMissingVaspOrder === true,
    count: dto.count ?? 0,
  }
}

export function parseVaspListingReadiness(dto: VaspListingReadinessDto): VaspListingReadiness {
  return {
    offeringId: dto?.offeringId ?? '',
    totalOrders: dto?.totalOrders ?? 0,
    ordersAwaitingIssuance: dto?.ordersAwaitingIssuance ?? 0,
    ordersNotYetReady: dto?.ordersNotYetReady ?? 0,
    ordersWithoutVaspOrder: dto?.ordersWithoutVaspOrder ?? 0,
    statusCounts: (dto?.statusCounts ?? []).map(parseVaspListingReadinessStatusCount),
    // `=== true` so an absent flag reads as not-ready. It informs a panel and
    // never gates a button, so the conservative default costs nothing.
    isReady: dto?.isReady === true,
  }
}

/**
 * `blockedOrders` defaults to an empty array, never to undefined: the UI branches
 * on its length to decide whether to say the raise was only partly unwound, and a
 * missing field must read as "nothing was blocked" rather than crash that branch.
 */
export function parseCancelRejectedListing(
  dto: CancelRejectedListingDto,
): CancelRejectedListingResult {
  return {
    offeringId: dto?.offeringId ?? '',
    cancelledOrderCount: dto?.cancelledOrderCount ?? 0,
    cancelledOrderIds: dto?.cancelledOrderIds ?? [],
    refundedOrderCount: dto?.refundedOrderCount ?? 0,
    refundTotal: dto?.refundTotal ?? '0',
    blockedOrders: (dto?.blockedOrders ?? []).map((entry) => ({
      orderId: entry?.orderId ?? '',
      reason: entry?.reason ?? 'The provider did not give a reason.',
    })),
  }
}

/**
 * Totals default to 0 and the list to empty, so a drawer that renders before the
 * response lands shows "no orders" rather than crashing on a map of undefined.
 */
export function parseOfferingOrders(dto: OfferingOrdersDto): OfferingOrders {
  return {
    orders: (dto?.orders ?? []).map((order) => ({
      id: str(order.id),
      orderReference: str(order.orderReference),
      orderDate: order.orderDate ?? null,
      orderStatus: str(order.orderStatus),
      orderProcessingStatus: order.orderProcessingStatus ?? null,
      tokenQuantity: str(order.tokenQuantity, '0'),
      netAmount: str(order.netAmount, '0'),
      currencyCode: str(order.currencyCode, 'AED'),
      investorId: str(order.investorId),
      investorName: str(order.investorName) || str(order.investorEmail),
      investorEmail: str(order.investorEmail),
      holdingId: order.holdingId ?? null,
      hasTitleDeed: order.hasTitleDeed === true,
      isTitleDeedAnnouncedOnly: order.isTitleDeedAnnouncedOnly === true,
    })),
    totalOrders: dto?.totalOrders ?? 0,
    completedOrders: dto?.completedOrders ?? 0,
    totalTokensIssued: str(dto?.totalTokensIssued, '0'),
    totalInvested: str(dto?.totalInvested, '0'),
    deedsIssued: dto?.deedsIssued ?? 0,
  }
}
